(function() {
  const root = document.querySelector('[data-karst-search-root]');
  const configScript = document.querySelector('[data-karst-search-config]');

  if (!root || !configScript) return;

  let config = {};
  try {
    config = JSON.parse(configScript.textContent || '{}');
  } catch (error) {
    console.error('Karst search config could not be parsed', error);
  }

  const panel = root.querySelector('[data-karst-search-panel]');
  const form = root.querySelector('[data-karst-search-form]');
  const input = root.querySelector('[data-karst-search-input]');
  const results = root.querySelector('[data-karst-search-results]');
  const trending = root.querySelector('[data-karst-search-trending]');
  const spinner = root.querySelector('[data-karst-search-spinner]');
  const status = root.querySelector('[data-karst-search-status]');
  const triggers = Array.from(document.querySelectorAll('[data-karst-search-open]'));
  const cache = new Map();
  let timer = null;
  let controller = null;
  let activeTrigger = null;
  let scrollY = 0;
  let displayedTerm = '';

  if (!panel || !form || !input || !results || !trending) return;

  function setLoading(isLoading) {
    results.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    spinner && spinner.setAttribute('data-visible', isLoading ? 'true' : 'false');
  }

  function announce(message) {
    if (status) status.textContent = message;
  }

  function lockScroll() {
    scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = '-' + scrollY + 'px';
    document.body.classList.add('karst-search-no-scroll');
  }

  function unlockScroll() {
    document.body.classList.remove('karst-search-no-scroll');
    document.body.style.top = '';
    window.scrollTo(0, scrollY);
  }

  function openSearch(trigger) {
    if (panel.getAttribute('aria-hidden') === 'false') return;

    activeTrigger = trigger || document.activeElement;
    panel.setAttribute('aria-hidden', 'false');
    panel.classList.add('karst-search-popup__panel--open');
    triggers.forEach((item) => item.setAttribute('aria-expanded', 'true'));
    lockScroll();

    window.requestAnimationFrame(() => input.focus());
  }

  function closeSearch() {
    if (panel.getAttribute('aria-hidden') !== 'false') return;

    if (controller) controller.abort();
    if (timer) window.clearTimeout(timer);
    controller = null;
    timer = null;
    setLoading(false);
    if (!results.hidden && displayedTerm === input.value.trim()) activateResults();
    else showTrending();
    panel.setAttribute('aria-hidden', 'true');
    panel.classList.remove('karst-search-popup__panel--open');
    input.setAttribute('aria-expanded', 'false');
    triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
    unlockScroll();

    if (activeTrigger && typeof activeTrigger.focus === 'function') activeTrigger.focus();
    activeTrigger = null;
  }

  function showTrending() {
    results.hidden = true;
    results.innerHTML = '';
    results.setAttribute('inert', '');
    results.setAttribute('aria-hidden', 'true');
    trending.hidden = false;
    displayedTerm = '';
    input.setAttribute('aria-expanded', 'false');
    setLoading(false);
    announce('Trending products shown');
  }

  function activateResults() {
    results.removeAttribute('inert');
    results.removeAttribute('aria-hidden');
    input.setAttribute('aria-expanded', 'true');
  }

  function deactivateResults() {
    results.setAttribute('inert', '');
    results.setAttribute('aria-hidden', 'true');
    input.setAttribute('aria-expanded', 'false');
  }

  function showResults(markup, term) {
    const fragment = new DOMParser().parseFromString(markup, 'text/html');
    const section = fragment.querySelector('#shopify-section-predictive-search');

    if (!section) throw new Error('Predictive search section was not returned');

    results.innerHTML = section.innerHTML;
    results.hidden = false;
    trending.hidden = true;
    displayedTerm = term;
    activateResults();
    setLoading(false);

    const resultRoot = results.querySelector('[data-predictive-result-count]');
    const count = resultRoot ? Number(resultRoot.getAttribute('data-predictive-result-count')) : 0;
    announce(count === 1 ? '1 suggestion available' : count + ' suggestions available');
  }

  function predictiveUrl(term) {
    const endpoint = config.predictiveSearchUrl || '/search/suggest';
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,page,article');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][prefix]', 'last');
    url.searchParams.set('section_id', 'predictive-search');
    return url.toString();
  }

  async function fetchSuggestions(term) {
    const cacheKey = predictiveUrl(term);

    if (cache.has(cacheKey)) {
      showResults(cache.get(cacheKey), term);
      return;
    }

    if (controller) controller.abort();
    controller = new AbortController();
    setLoading(true);

    try {
      const response = await fetch(predictiveUrl(term), {signal: controller.signal});
      if (!response.ok) throw new Error('Predictive search failed with status ' + response.status);

      const markup = await response.text();
      if (input.value.trim() !== term) return;

      cache.set(cacheKey, markup);
      showResults(markup, term);
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      console.error(error);
      if (input.value.trim() === term) {
        showTrending();
        announce('Suggestions are unavailable. Press Enter to search.');
      }
    }
  }

  function handleInput() {
    const term = input.value.trim();

    if (timer) window.clearTimeout(timer);
    if (controller) controller.abort();

    if (!term) {
      showTrending();
      return;
    }

    deactivateResults();
    setLoading(true);
    timer = window.setTimeout(() => fetchSuggestions(term), 250);
  }

  function visibleFocusableElements() {
    return Array.from(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.closest('[hidden]'));
  }

  function suggestionLinks() {
    if (results.hasAttribute('inert')) return [];
    return Array.from(results.querySelectorAll('[data-karst-search-result]'));
  }

  function handleDialogKeydown(event) {
    if (panel.getAttribute('aria-hidden') !== 'false') return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeSearch();
      return;
    }

    if (event.key === 'Tab') {
      const focusable = visibleFocusableElements();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const links = suggestionLinks();
    if (!links.length) return;

    const currentIndex = links.indexOf(document.activeElement);
    if (document.activeElement === input) {
      event.preventDefault();
      links[event.key === 'ArrowDown' ? 0 : links.length - 1].focus();
      return;
    }

    if (currentIndex >= 0) {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = (currentIndex + direction + links.length) % links.length;
      links[nextIndex].focus();
    }
  }

  input.addEventListener('input', handleInput);

  triggers.forEach((trigger) => {
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      openSearch(trigger);
    });
  });

  root.querySelectorAll('[data-karst-search-close]').forEach((button) => {
    button.addEventListener('click', closeSearch);
  });

  results.addEventListener('click', (event) => {
    const result = event.target.closest('[data-karst-search-result]');
    if (!result) return;

    const term = input.value.trim();
    if (!window.Shopify || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') {
      closeSearch();
      return;
    }

    Shopify.analytics.publish('karst_predictive_search_submitted', {
      search_term: term,
      search_result_type: result.getAttribute('data-result-type') || '',
      search_result_title: result.getAttribute('data-result-title') || ''
    });
    closeSearch();
  });

  document.addEventListener('keydown', handleDialogKeydown);
})();
